/**
 * Ownership of the teacher-side PubNub connection (SOC-5879, realtime ADR).
 *
 * Historically every page template created `window.pubnub` from a static
 * pubnub-4.20.0.min.js script tag — teacher.pug even created a second
 * instance over the one base.pug had already made. The client is now created
 * exactly once, here, from the bundled SDK, with a stable identity pinned at
 * construction (no setUUID race with the first subscribe).
 *
 * `window.pubnub` is still assigned as a migration alias: legacy
 * Messaging.js and the React pages that pass `window.pubnub` into
 * useActivity read it. The alias — and this note — are deleted with
 * Messaging.js at the end of the strangler migration.
 */
import PubNub from 'pubnub';

import { realtimeLog } from './debug';

let client: PubNub | null = null;

const UUID_STORAGE_KEY = 'socrative-teacher-pubnub-uuid';

/**
 * Teachers have no user_uuid (that's a student-only field) and their
 * publicid isn't available client-side before room join, so occupancy dedupe
 * uses a persisted per-browser id — it only needs stability, not identity.
 * Without it the SDK generates a random uuid per page load, so every refresh
 * strands the previous uuid in channel occupancy until the presence timeout
 * (~5 min), inflating the open-room student count (`occupancy - 1` assumes
 * exactly one teacher connection) and double-counting multi-tab teachers.
 * The `t-` prefix can never collide with a student user_uuid and makes
 * teacher occupants obvious in [realtime] traces.
 */
function stableTeacherUuid(): string {
  const random = window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  try {
    let uuid = window.localStorage.getItem(UUID_STORAGE_KEY);
    if (!uuid) {
      uuid = `t-${random}`;
      window.localStorage.setItem(UUID_STORAGE_KEY, uuid);
    }
    return uuid;
  } catch (e) {
    // Storage unavailable (privacy mode) — a per-page-load id stands and
    // behavior degrades to the pre-fix state for this session only.
    return `t-${random}`;
  }
}

/**
 * Create the app's single PubNub instance. Idempotent — safe to call from
 * every entry point that renders realtime consumers (teacher, showbie
 * wrapper). Must run before anything subscribes (room.join, React render).
 *
 * Returns null when no subscribe key is configured; realtime features then
 * stay inert, matching the old behavior when the static lib failed to load.
 */
export function initRealtimeClient(): PubNub | null {
  if (client) {
    return client;
  }

  const subscribeKey = window.APP_CONFIG?.PUBNUB_SUBSCRIBE_KEY;
  if (!subscribeKey) {
    realtimeLog('client: no PUBNUB_SUBSCRIBE_KEY — realtime disabled');
    return null;
  }

  client = new PubNub({
    subscribeKey,
    // Parity with the retired template config: the SDK's default origins are
    // psN.pndsn.com, and school-network allowlists were built against this.
    origin: 'pubsub.pubnub.com',
    ssl: true,
    userId: stableTeacherUuid(),
    // On a network drop the SDK disconnects and, when the network is back,
    // resumes every subscription where it left off (the SDK default, set
    // here so it isn't copied from the student app). With restore off it
    // destroys the client instead, and channelManager, which only tracks
    // what it asked for, would never resubscribe.
    restore: true,
  });

  window.pubnub = client; // legacy alias — see module note

  realtimeLog('client: initialized', client.userId);
  return client;
}

/** The instance created by initRealtimeClient, or null before init. */
export function getRealtimeClient(): PubNub | null {
  return client;
}

/** Test/teardown hook. Does not tear down subscriptions — tests own that. */
export function resetRealtimeClient() {
  client = null;
}
